/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Query Prefetching Utilities
 *
 * Prefetch related data to reduce loading times when navigating between pages.
 * This eliminates the "loading spinner" experience for users.
 */

import { QueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { listCommits } from '@/lib/api/commits'

/**
 * Prefetch all data for a project (sequences, clips, commits)
 * Call this when hovering over a project card or navigating to a project
 */
export async function prefetchProjectData(
  queryClient: QueryClient,
  projectId: string
) {
  // Prefetch sequences
  queryClient.prefetchQuery({
    queryKey: ['sequences', 'default', projectId],
    queryFn: async () => {
      const { data: existingSequences, error: fetchError } = await supabase
        .from('sequences')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true })
        .limit(1)

      if (fetchError) throw fetchError

      if (existingSequences && existingSequences.length > 0) {
        return existingSequences[0]
      }

      // Don't create sequence during prefetch, just return null
      return null
    },
    staleTime: 1000 * 60 * 5,
  })

  // Prefetch clips for the project
  queryClient.prefetchQuery({
    queryKey: ['clips', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
    staleTime: 1000 * 60 * 5,
  })

  // Prefetch activity logs
  queryClient.prefetchQuery({
    queryKey: ['activityLogs', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      return data
    },
    staleTime: 1000 * 60 * 2, // Activity logs are fresher
  })
}

/**
 * Prefetch commits for a sequence
 * Call this when a sequence is loaded
 */
export async function prefetchSequenceCommits(
  queryClient: QueryClient,
  sequenceId: string
) {
  return queryClient.prefetchQuery({
    queryKey: ['commits', sequenceId],
    queryFn: async () => {
      try {
        return await listCommits(sequenceId)
      } catch (error) {
        // Don't throw during prefetch - just return empty
        console.warn('Failed to prefetch commits:', error)
        return { commits: [], total: 0, sequence_id: sequenceId }
      }
    },
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Prefetch a single project
 * Useful when hovering over project cards
 */
export async function prefetchProject(
  queryClient: QueryClient,
  projectId: string
) {
  return queryClient.prefetchQuery({
    queryKey: ['projects', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle()

      if (error) throw error
      return data
    },
    staleTime: 1000 * 60 * 10, // Projects change less frequently
  })
}
